/**
 * Email notification preference toggle (Email Foundation, Wave 3).
 *
 * Profile-page card exposing users.email_notifications_enabled for the
 * logged-in user. Zoneless-safe: every async result lands in a signal, so the
 * template re-renders without manual change detection. Saves optimistically
 * and rolls back to the last confirmed value if the PUT fails.
 *
 * The one-click unsubscribe endpoint flips the same column, so a user who
 * unsubscribed from an email re-enables it right here.
 */
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ProfileService } from '../profile.service';

@Component({
  selector: 'app-email-notification-settings',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './email-notification-settings.component.html',
  styleUrls: ['./email-notification-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmailNotificationSettingsComponent implements OnInit {
  private readonly profileService = inject(ProfileService);
  private readonly destroyRef = inject(DestroyRef);

  /** Last confirmed (or optimistic) preference. Default ON, matching the DB. */
  readonly enabled = signal(true);
  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);

  ngOnInit(): void {
    this.profileService
      .getEmailNotificationsEnabled()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (enabled) => {
          this.enabled.set(enabled);
          this.loading.set(false);
        },
        error: () => {
          // Keep the default-ON toggle usable; the save path re-syncs.
          this.loading.set(false);
          this.errorMessage.set('Could not load your email notification preference.');
        },
      });
  }

  onToggle(event: Event): void {
    const input = event.target as HTMLInputElement;
    const next = input.checked;
    const previous = this.enabled();

    this.enabled.set(next); // optimistic
    this.saving.set(true);
    this.errorMessage.set(null);

    this.profileService
      .setEmailNotificationsEnabled(next)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (confirmed) => {
          this.enabled.set(confirmed);
          this.saving.set(false);
        },
        error: () => {
          // Roll back the optimistic flip. The user's click flipped the native
          // checkbox itself, so when the rollback value equals the last
          // RENDERED binding value the [checked] binding no-ops — write the
          // native property too so the visual state always reverts.
          this.enabled.set(previous);
          input.checked = previous;
          this.saving.set(false);
          this.errorMessage.set('Could not save your email notification preference. Please try again.');
        },
      });
  }
}
